// client/src/wordpress_api.ts

import axios, { AxiosResponse } from 'axios'
import { Map } from 'immutable'
import { Breadcrumb } from './types_and_parser/extra_types.mod'
import { Acf } from './types_and_parser/types.mod'
import { VideoProps } from '../video'
import { ImageSize as PublicSiteImageSize } from './../cms/blocks/types.gen'
import { ExtraImages } from '../public_site/utils/extra_images.mod';
import { Fun, Unit, HttpResult } from 'widgets-for-react'
import { CaseResponse } from '../../wp_types'
import { DataLayerHandler } from '../../helpers'

export interface Headers {
  'X-WP-Nonce'?: string
}

export namespace Store {
  export let store: Map<string, any> = Map<string, any>()

  export function read<T>(key: string): T {
    return store.get(key)
  }

  export function write<T>(key: string, data: T) {
    store = store.set(key, data)
    return data
  }
}

export namespace WordPress {
  export type PostType = 'post' | 'page' | 'news_article' | 'vacancy'

  export type PostId = number
  export type AuthorId = number
  export type CategoryId = number
  export type TagId = number
  export type AttachmentId = number
  export type DateTimeString = string // '2018-03-04T14:51:59'
  export type Url = string
  export type CommentStatus = 'open' | 'closed'
  export type PingStatus = 'open' | 'closed'
  export type TermId = number

  export interface PostObject {
    ID: PostId
    post_author: AuthorId
    post_date: DateTimeString
    post_date_gmt: DateTimeString
    post_content: string
    post_title: string
    post_excerpt: string
    post_status: 'publish' | 'pending' | 'draft' | 'auto-draft' | 'future' | 'private' | 'inherit' | 'trash'
    comment_status: CommentStatus
    ping_status: PingStatus
    post_password: string
    post_name: string
    to_ping: string
    pinged: string
    post_modified: DateTimeString
    post_modified_gmt: DateTimeString
    post_content_filtered: string
    post_parent: PostId
    guid: string
    menu_order: number
    post_type: string
    post_mime_type: string
    comment_count: number
  }

  export interface PostRequestArguments {
    after?: string // ISO8601
    author?: number
    author_exclude?: number
    before?: Date // ISO8601
    categories?: number
    categories_exclude?: number
    exclude?: number
    include?: string // commas separated list 1,2,3
    order?: 'asc' | 'desc'
    orderby?:
    | 'author'
    | 'date'
    | 'id'
    | 'include'
    | 'modified'
    | 'parent'
    | 'relevance'
    | 'slug'
    | 'include_slugs'
    | 'title'
    | 'menu_order'
    | 'random'
    page?: number
    per_page?: number
    search?: string
    slug?: string
    status?: 'publish' | 'pending' | 'draft' | 'auto-draft' | 'future' | 'private' | 'inherit' | 'trash'
    sticky?: number
    tags?: number
    tags_exclude?: number
    _embed?: boolean
    themes?: number
    articles?: number
    _wpnonce?: string
    pagename?: string
    post_type?: string // comma seperated post type names
  }

  export type PostResponse<PostType> = {
    id: PostId
    type: PostType
    date: DateTimeString
    date_gmt: DateTimeString
    guid: {
      rendered: string
    }
    modified: DateTimeString
    modified_gmt: DateTimeString
    slug: string
    status: 'publish' | 'pending' | 'draft' | 'auto-draft' | 'future' | 'private' | 'inherit' | 'trash'
    link: Url
    title: {
      rendered: string
    }
    content: {
      rendered: string
      protected: boolean
    }
    excerpt: string
    author: AuthorId
    parent: false | PostObject
    featured_media: AttachmentId
    comment_status: CommentStatus
    ping_status: PingStatus
    sticky: boolean
    format: 'standard' | 'aside' | 'chat' | 'gallery' | 'link' | 'image' | 'quote' | 'status' | 'video' | 'audio'
    //meta: []
    yoast?: YoastResponse
    categories: Array<CategoryId>
    tags: Array<TagId>
    acf: {
      buildingblocks?: Array<Acf.Block>
    }
    breadcrumbs: Breadcrumb[]
  }

  export interface PageResponse extends WordPress.PostResponse<'page'> {
    acf: {
      hero_intro?: string
      hero_subtitle?: string
      buildingblocks?: Array<Acf.Block>
      title: string
      intro_text?: string
      sub_text?: string
      kind_media: 'video' | 'image' | 'none'
      image?: {
        id?: number
        sizes: { [P in PublicSiteImageSize<ExtraImages>]: string }
      }
      image_alt?: string
      video?: string
      links: Array<{
        link_title: Acf.Text
        link_url: Acf.Text
      }>
    }
    is_front: boolean
  }

  export interface NewsArticleResponse extends WordPress.PostResponse<'news_article'> {
    acf: {
      hero_intro?: string
      hero_subtitle?: string
      buildingblocks?: Array<Acf.Block>
      title: string
      intro_text?: string
      sub_text?: string
      kind_media: 'video' | 'image' | 'none'
      image?: {
        id: number
        sizes: { [P in PublicSiteImageSize<ExtraImages>]: string }
      }
      image_alt?: string
      video?: string
      links: Array<{
        link_title: Acf.Text
        link_url: Acf.Text
      }>
      author_name: Acf.Text
      author_job: Acf.Text
      author_image: Acf.ImageArray
    }
  }

  export interface CaseResponse extends WordPress.PostResponse<'case'> {
    acf: {
      hero_intro?: string
      hero_subtitle?: string
      buildingblocks?: Array<Acf.Block>
      title: string
      intro_text?: string
      sub_text?: string
      kind_media: 'video' | 'image' | 'none'
      image?: {
        id: number
        sizes: { [P in PublicSiteImageSize<ExtraImages>]: string }
      }
      image_alt?: string
      video?: string
      links: Array<{
        link_title: Acf.Text
        link_url: Acf.Text
      }>
      author_name: Acf.Text
      author_job: Acf.Text
      author_image: Acf.ImageArray
    }
  }

  export type navLink = {
    item: Acf.Link
  }

  export type menuItem = {
    submenu: "no" | "gridblocks" | "linklist"
    link?: Acf.Link
    title?: Acf.Text
    linklist_1_intro?: string
    linklist_1?: { link: Acf.Link }[]
    linklist_2_intro?: string
    linklist_2?: { link: Acf.Link }[]
    gridblocks?: {
      subtitle?: string
      title: string
      text: string
      link: Acf.Link
    }[]
  }

  export type OptionsResponse = {
    acf: {
      first_title?: string
      first_items?: navLink[]
      second_title?: string
      second_items?: navLink[]
      third_title?: string
      third_items?: navLink[]
      fourth_title?: string
      fourth_items?: navLink[]
      menu?: menuItem[]
      sub_items?: { link: Acf.Link }[]
      global_address: string
      global_contact: string
    }
  }

  export type PostTypeResponse = PageResponse | NewsArticleResponse | CaseResponse

  export type ArticleResponse = NewsArticleResponse

  export interface TaxonomyResponse {
    id: number
    count: number
    description: string
    link: string
    name: string
    slug: string
    taxonomy: string
    parent: number
    meta: {}
    acf?: {}
  }

  export type TaxonomyTerm = {
    count: number
    description: string
    filter: string
    name: string
    parent: number
    slug: string
    taxonomy: string
    term_group: number
    term_id: number
    term_taxonomy_id: number
  }

  export class Image {
    id: AttachmentId
    date: DateTimeString
    date_gmt: DateTimeString
    guid: {
      rendered: Url
    }
    modified: DateTimeString
    modified_gmt: DateTimeString
    slug: string
    status: 'inherit'
    type: 'attachment'
    link: Url
    title: {
      rendered: string
    }
    author: AuthorId
    comment_status: CommentStatus
    ping_status: PingStatus
    template: ''
    meta: Array<any> // TODO
    acf?: Array<any> // TODO
    description: {
      rendered: string
    }
    caption: {
      rendered: string
    }
    alt_text: string
    media_type: 'image' // TODO
    mime_type: 'image/jpeg' // TODO
    media_details: {
      width: number
      height: number
      file: string
      sizes: ImageSizes
      image_meta: {
        aperture: string
        credit: string
        camera: string
        caption: string
        created_timestamp: string
        copyright: string
        focal_length: string
        iso: string
        shutter_speed: string
        title: string
        orientation: string
        keywords: Array<string>
      }
    }
    post: PostId
    source_url: Url
    _links: {
      self: [
        {
          href: Url
        }
      ]
      collection: [
        {
          href: Url
        }
      ]
      about: [
        {
          href: Url
        }
      ]
      author: [
        {
          embeddable: boolean
          href: Url
        }
      ]
      replies: [
        {
          embeddable: boolean
          href: Url
        }
      ]
    }
  }

  export interface ImageSize {
    file: string
    width: number
    height: 150
    mime_type: string
    source_url: string
  }

  export type ImageSizes = {
    full: ImageSize
    large: ImageSize
    medium_large: ImageSize
    medium: ImageSize
    thumbnail: ImageSize
    content: ImageSize
    teaser: ImageSize
    logowall: ImageSize
    highlight: ImageSize
  }

  export type MediaResponse = Image

  export type MenuItem = {
    ID: MenuId
    order: number
    parent: number
    title: string
    url: Url
    attr: string
    target: string
    classes: string
    xfn: string
    description: string
    object_id: number
    object: string
    type: string
    type_label: string
    children: Array<MenuItem>
  }

  export type MenuId = number
  export type MenuLocationResponse = Array<MenuItem>
  export type MenuLocationsResponse = {
    [key: string]: {
      ID: MenuId
      label: string
      meta: {
        links: {
          collection: Url
          // self: Url // self gives back wrong namespace, don't use this
        }
      }
    }
  }

  export type YoastResponse = {
    focuskw?: string
    title?: string
    metadesc?: string
    linkdex?: string
    metakeywords?: string
    meta_robots_noindex?: string
    meta_robots_nofollow?: string
    meta_robots_adv?: string
    canonical?: string
    redirect?: string
    opengraph_title?: string
    opengraph_description?: string
    opengraph_image?: string
    twitter_title?: string
    twitter_description?: string
    twitter_image?: string
    breadcrumb?: string
  }

  function RestApislugForPostType(post_type: PostType): string {
    switch (post_type) {
      case 'post':
        return 'posts'
      case 'page':
        return 'pages'
      case 'news_article':
        return 'news'
      case 'vacancy':
        return 'vacancy'
    }
  }

  export function PostTypeForSlug(post_type: string): PostType {
    switch (post_type) {
      case 'posts':
        return 'post'
      case '':
        return 'page'
      case 'news':
        return 'news_article'
      case 'vacancy':
        return 'vacancy'
    }
  }

  export function api<R>() {
    return new Api<R>()
  }

  export class Api<R> {
    args: PostRequestArguments = {}
    endpoint: string
    post_type: PostType

    constructor() {
      return this
    }
    select(post_type: PostType) {
      this.post_type = post_type
      // this.endpoint = `${window.wp.api}/wp/v2/${RestApislugForPostType(post_type)}`
      return this
    }

    where(args: PostRequestArguments) {
      this.args = args
      return this
    }

    getOne(): Promise<AxiosResponse<R>> {
      // const storeKey = `${window.wp.api}/wp/v2/${this.post_type}${JSON.stringify(this.args)}`
      // const val = Store.read<AxiosResponse<R>>(storeKey)
      // if (val) {
      //   return Promise.resolve(val)
      // }

      let params: { [key: string]: string } = {}
      // if (window.wp.is_user_logged_in) {
      //   params['_wpnonce'] = window.wp.nonce
      // }

      return axios
        .get<Array<R>>(this.endpoint, { params: this.args, withCredentials: true }) //
        .then(function (response): Promise<AxiosResponse<R>> {
          if (response && response.data && response.data[0]) {
            let result: AxiosResponse<R> = {
              ...response,
              data: response.data[0]
            }

            // Store.write(storeKey, result)
            return Promise.resolve(result)
          } else {
            return Promise.reject('No Data')
          }
        })
        .catch((error: string) => {
          console.log('getOne', error)
          return Promise.reject(error)
        })
    }

    getMultiple(): Promise<AxiosResponse<Array<R>>> {
      // const storeKey = `${window.wp.api}/wp/v2/${this.post_type}${JSON.stringify(this.args)}`
      // const val = Store.read<AxiosResponse<Array<R>>>(storeKey)
      // if (val) {
      //   return Promise.resolve(val)
      // }

      let params: { [key: string]: string } = {}
      // if (window.wp.is_user_logged_in) {
      //   params['_wpnonce'] = window.wp.nonce
      // }

      return axios
        .get<Array<R>>(this.endpoint, { params: this.args, withCredentials: true }) //
        .then(function (response) {
          if (response) {
            // Store.write(storeKey, response)
            return Promise.resolve(response)
          } else {
            return Promise.reject('Failure')
          }
        })
    }

    findById(id: string | number): Promise<AxiosResponse<R>> {
      const storeKey = `${this.endpoint}/wp/v2/${this.post_type}/${id}`
      const val = Store.read<AxiosResponse<R>>(storeKey)
      if (val) {
        return Promise.resolve(val)
      }

      let params = this.args
      // if (window.wp.is_user_logged_in) {
      //   params['_wpnonce'] = window.wp.nonce
      // }

      return axios
        .get<R>(`${this.endpoint}/${id}`, { params: params, withCredentials: true }) //
        .then(function (response) {
          if (response && response.data) {
            Store.write(storeKey, response)
            return Promise.resolve(response)
          } else {
            return Promise.reject('Failure')
          }
        })
    }

    frontpage(): Promise<AxiosResponse<R>> {
      const request_url = `/wp-json/wp/v2/frontpage`
      const val = Store.read<AxiosResponse<R>>(request_url)
      if (val) {
        return Promise.resolve(val)
      }

      return axios
        .get<R>(request_url, { withCredentials: true }) //
        .then(function (response) {
          if (response && response.data) {
            Store.write(request_url, response)
            return Promise.resolve(response)
          } else {
            return Promise.reject('Failure')
          }
        })
    }

    current(path: string): Promise<AxiosResponse<PostTypeResponse>> {
      const request_url = `/wp-json/wp/v2/current`
      const params = {
        url: `/${path ? path : ''}`
      }
      const store_key = `/wp-json/wp/v2/current${JSON.stringify(params)}`
      const val = Store.read<AxiosResponse<PostTypeResponse>>(store_key)

      if (val) return Promise.resolve(val)

      return axios
        .get<PostTypeResponse>(request_url, { params, withCredentials: true }) //
        .then(function (response) {
          if (response && response.data) {
            Store.write(store_key, response)
            DataLayerHandler({
              content: {
                contentInfo: {
                  language: 'nl',
                  publishedAt: response?.data?.date,
                  title: response?.data?.title?.rendered,
                  authors: response?.data?.author?.toString(),
                  tags: response?.data?.tags?.reduce((xs, x) => xs + x + ";", ""),
                  contentId: response?.data?.id?.toString(),
                }
              },
              page: {
                pageinfo: {
                  destinationURL: document.location.href,
                  pageName: response?.data?.title?.rendered,
                  pageType: response?.data?.type,
                }
              },
              session: {
                authentication: {
                  loginStatus: false,
                  subscriberId: '',
                  subscriptions: ''
                }
              }
            })
            return Promise.resolve(response)
          } else if (response.status != 200) {
            DataLayerHandler({
              event: 'trackEvent', 
              eventCategory: 'error'
            })
            return Promise.reject('Failure')
          }
          else {
            DataLayerHandler({
              event: 'trackEvent', 
              eventCategory: 'error'
            })
            return Promise.reject('Failure')
          }
        })
        .catch(e => {
          DataLayerHandler({
            event: 'trackEvent', 
            eventCategory: 'error'
          })
          return Promise.reject(e)
        })
    }

    anypost(id: number): Promise<AxiosResponse<PostTypeResponse>> {
      const request_url = `/wp-json/wp/v2/anypost/${id}`
      const val = Store.read<AxiosResponse<PostTypeResponse>>(request_url)

      if (val) {
        return Promise.resolve(val)
      }

      return axios
        .get<PostTypeResponse>(request_url, { withCredentials: true }) //
        .then(function (response) {
          if (response && response.data) {
            Store.write(request_url, response)
            return Promise.resolve(response)
          } else {
            return Promise.reject('Failure')
          }
        })
    }

    options(id: string): Promise<AxiosResponse<OptionsResponse>> {
      const request_url = `/wp-json/acf/v3/options/${id}`
      const val = Store.read<AxiosResponse<OptionsResponse>>(request_url)

      if (val) {
        return Promise.resolve(val)
      }

      return axios
        .get<OptionsResponse>(request_url, { withCredentials: true }) //
        .then(function (response) {
          if (response && response.data) {
            Store.write(request_url, response)
            return Promise.resolve(response)
          } else {
            return Promise.reject('Failure')
          }
        })
    }
  }

  export function media<R>() {
    return new MediaApi<R>()
  }

  export class MediaApi<R> {
    endpoint: string
    id: number

    constructor() {
      return this
    }

    getOne(): Promise<AxiosResponse<R>> {
      const storeKey = `${this.endpoint}/wp-json/wp/v2/media/${this.id}`
      const val = Store.read<AxiosResponse<R>>(storeKey)
      if (val) {
        return Promise.resolve(val)
      }

      let params: { [key: string]: string } = {}
      // if (window.wp.is_user_logged_in) {
      //   params['_wpnonce'] = window.wp.nonce
      // }

      return axios
        .get<Array<R>>(this.endpoint, { withCredentials: true }) //
        .then(function (response): Promise<AxiosResponse<R>> {
          if (response && response.data && response.data[0]) {
            let result: AxiosResponse<R> = {
              ...response,
              data: response.data[0]
            }

            Store.write(storeKey, result)
            return Promise.resolve(result)
          } else {
            return Promise.reject('No Data')
          }
        })
        .catch(error => Promise.reject(error))
    }

    findById(id: string | number): Promise<AxiosResponse<R>> {
      this.endpoint = `${window.wp.api}/wp/v2/media`

      const storeKey = `${this.endpoint}/wp/v2/media/${id}`
      const val = Store.read<AxiosResponse<R>>(storeKey)
      if (val) {
        return Promise.resolve(val)
      }

      let params = {}
      // if (window.wp.is_user_logged_in) {
      //   params['_wpnonce'] = window.wp.nonce
      // }

      return axios
        .get<R>(`${this.endpoint}/${id}`, { params: params, withCredentials: true }) //
        .then(function (response) {
          if (response && response.data) {
            Store.write(storeKey, response)
            return Promise.resolve(response)
          } else {
            return Promise.reject('Failure')
          }
        })
        .catch(error => Promise.reject(error))
    }
  }
}

export const getNewsArticles: () => Promise<HttpResult<WordPress.NewsArticleResponse[]>> = async () => {
  try {
    let articles: WordPress.NewsArticleResponse[] = await (await fetch(`/wp-json/wp/v2/news_articles?per_page=21`, { credentials: "include" })).json()

    articles = articles.sort((a, b) => b.date.localeCompare(a.date))

    return {
      kind: 'result',
      status: 200,
      value: articles
    }

  } catch (e) {
    console.error(e)

    return {
      kind: 'failed',
      status: 500
    }
  }
}

export const getNewsArticle: (id: WordPress.PostTypeResponse[]) => Promise<HttpResult<WordPress.NewsArticleResponse[]>> = async (id) => {
  try {
    let articles = Array(id.length);
    for (let i = 0; i < id.length; i++) {
      articles[i] = await (await fetch(`/wp-json/wp/v2/news_articles/${(id[i] as any).article.ID}`, { credentials: "include" })).json()
    }
    return {
      kind: 'result',
      status: 200,
      value: articles
    }

  } catch (e) {
    console.error(e)

    return {
      kind: 'failed',
      status: 500
    }
  }
}

export const getHighlight: (id: number, post_type: string) => Promise<HttpResult<WordPress.NewsArticleResponse | WordPress.CaseResponse>> = async (id, post_type) => {
  try {
    let item: WordPress.NewsArticleResponse | WordPress.CaseResponse;
    item = await (await fetch(`/wp-json/wp/v2/${post_type}s/${id}`, { credentials: "include" })).json()
    return {
      kind: 'result',
      status: 200,
      value: item
    }

  } catch (e) {
    console.error(e)

    return {
      kind: 'failed',
      status: 500
    }
  }
}

export const getCases = async (categories: number[]): Promise<HttpResult<WordPress.CaseResponse[]>> => {
  try {
    const categoriesQuery = categories.map(n => `&case_category[]=${n}`)
    const p: WordPress.CaseResponse[] = await (await fetch(`/wp-json/wp/v2/cases?per_page=3${categoriesQuery.join('')}`)).json()

    return {
      kind: 'result',
      status: 200,
      value: p
    }
  } catch (e) {
    console.error(e)

    return {
      kind: 'failed',
      status: 500
    }
  }
}
